<template>
  <div class="fts-listing-tools">
    <div class="listing-tools-container">
      <!-- query -->
      <fts-query-designer
        :modules="modules"
        :states="states"
        :information-source="filterColumns"
        :filter="filters"
        mode="view"
        v-if="isQueryRight"
      />
      <!-- controls -->
      <div class="listing-controls">
        <!-- sort -->
        <fts-sort
          :modules="modules"
          :fields="columns"
          v-model="mutableSorts"
          :total-items-exceed="totalItemsExceed"
          @closed="updateSorts"
          v-if="isSortRight"
        />
        <!-- refresh -->
        <fts-tippy
          content="Refresh list"
          append-trigger-class="listing-control-trigger"
          class="listing-control"
          :class="{'listing-control--loading': loading}"
          @trigger:click="!loading && refresh()"
          v-if="isRefreshRight"
        >
          <font-awesome-icon
            :icon="['fas', 'sync-alt']"
            fixed-width
            :spin="loading"
          />
        </fts-tippy>
        <!-- import -->
        <fts-tippy
          content="Import data"
          append-trigger-class="listing-control-trigger"
          class="listing-control"
          @trigger:click="importData"
          v-if="isImportRight"
        >
          <font-awesome-icon :icon="['fas', 'file-upload']" fixed-width />
        </fts-tippy>
        <!-- export -->
        <fts-tippy
          content="Export data"
          append-trigger-class="listing-control-trigger"
          class="listing-control"
          @trigger:click="exportData"
          v-if="isExportRight"
        >
          <font-awesome-icon :icon="['fas', 'file-download']" fixed-width />
          <!-- temp for everything listing -->
          <fts-release-badge alpha class="fts-align-top" v-if="listingId === 'everything' && isInternal" />
        </fts-tippy>
        <!-- dashboard widget -->
        <fts-tippy
          content="Analytics"
          append-trigger-class="listing-control-trigger"
          class="listing-control"
          @trigger:click="openWidgetDesignPreviewModal"
          v-if="isDashboardWidgetRight"
        >
          <font-awesome-icon :icon="['fas', 'chart-column']" fixed-width />
          <!-- temp for everything listing -->
          <fts-release-badge alpha class="fts-align-top" v-if="listingId === 'everything' && isInternal" />
        </fts-tippy>
        <!-- dashboard calendar -->
        <fts-tippy
          content="Add my calendar"
          append-trigger-class="listing-control-trigger"
          class="listing-control"
          @trigger:click="openMyCalendarFormModal"
          v-if="isDashboardCalendarRight"
        >
          <font-awesome-icon :icon="['far', 'calendar-plus']" fixed-width />
        </fts-tippy>
        <!-- bulk -->
        <fts-tippy
          :content="`${enableBulk ? 'Turn off' : 'Turn on'} bulk actions`"
          append-trigger-class="listing-control-trigger"
          class="listing-control"
          :class="{'listing-control--active': enableBulk}"
          @trigger:click="$emit('bulk')"
          v-if="isBulkRight"
        >
          <font-awesome-icon :icon="['fas', 'tasks']" fixed-width />
        </fts-tippy>
      </div>
    </div>
    <!-- import -->
    <fts-import
      :append-to-body="true"
      :module-id="moduleId"
      :module-import="moduleImport"
      @importDone="refresh"
      ref="ftsImport"
      v-if="isImportRight"
    />
    <!-- add my calendar -->
    <fts-my-calendar-form-modal
      :append-to-body="true"
      :workspace-id="workspaceId"
      :module-id="moduleId"
      :list-id="listingId"
      :options="getDateTimeColumns"
      ref="ftsMyCalendarFormModal"
    />
    <!-- add dashboard widget -->
    <fts-widget-design-preview-modal
      :append-to-body="true"
      :modules="modules"
      :states="states"
      :users="users"
      ref="ftsWidgetDesignPreviewModal"
    />
  </div>
</template>

<script>
import { authorizationStore } from '../../store/authorizationStore';
import { isCapableOf, SYSTEM_CAPABILITY } from '../../commons/flags';

import FTSImport from '../FTSImport';
import FTSMyCalendarFormModal from '../FTSMyCalendarFormModal';
import FTSQueryDesigner from '../../design/FTSQueryDesigner';
import FTSReleaseBadge from '../FTSReleaseBadge';
import FTSSort from '../FTSSort';
import FTSTippy from '../FTSTippy';
import FTSWidgetDesignPreviewModal from '../../dashboard/design/FTSWidgetDesignPreviewModal';

export default {
  components: {
    "fts-import": FTSImport,
    "fts-my-calendar-form-modal": FTSMyCalendarFormModal,
    "fts-query-designer": FTSQueryDesigner,
    "fts-release-badge": FTSReleaseBadge,
    "fts-sort": FTSSort,
    "fts-tippy": FTSTippy,
    "fts-import": FTSImport,
    "fts-widget-design-preview-modal": FTSWidgetDesignPreviewModal
  },
  props: {
    workspaceId: [String, Number],
    workspaceBeta: Boolean,
    workspaceContext: Object,
    modules: Array,
    states: Array,
    users: Array,
    moduleId: [String, Number],
    moduleImport: Object,
    listingId: String,
    columns: Array,
    filterColumns: Array,
    dateTimeColumns: Array,
    filters: Object,
    skipFiltersWatch: Boolean,
    sorts: [Object, Array],
    standardActivities: Array,
    enableBulk: Boolean,
    loading: Boolean,
    excludes: {
      type: Array,
      default: () => []
    },
    totalItemsExceed: Boolean
  },
  methods: {
    isCapableOf(type) {
      return isCapableOf(type, this.modules, this.moduleId);
    },
    refresh() {
      this.$emit("refresh");
    },
    updateSorts() {
      this.$emit("sorts:changed");
    },
    importData() {
      this.$refs.ftsImport.open();
    },
    exportData() {
      const payload = {
        parameters: {
          list: this.listingId,
          vectorId: this.moduleId,
          queryFilters: this.filters,
          context: this.workspaceContext != null ?
            this.workspaceContext.id :
            null
        }
      }
      const encodedUri = `/service/htmltemplateprocessing/document?documentmodel=${JSON.stringify(payload)}`;
      const url = `/#/htmlViewer?file=${encodeURIComponent(encodedUri)}`;
      window.open(url, "_blank");
    },
    openWidgetDesignPreviewModal() {
      this.$refs.ftsWidgetDesignPreviewModal.show({
        moduleId: this.moduleId,
        listingId: this.listingId,
        filters: this.filters,
        source: "listView"
      });
    },
    openMyCalendarFormModal() {
      this.$refs.ftsMyCalendarFormModal.show();
    },
    cancelDebouncedFilters() {
      if (this.debouncedFilters && this.debouncedFilters.cancel) {
        this.debouncedFilters.cancel();
      }
    }
  },
  computed: {
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    isBeta() {
      return authorizationStore.getters.isBeta || this.workspaceBeta;
    },
    getDateTimeColumns() {
      if (Array.isArray(this.dateTimeColumns) && this.dateTimeColumns.length) {
        return this.dateTimeColumns;
      }

      return this.columns
        .filter(
          x => (x.type === 5 || x.type === 6) && !x.system
        )
        .concat([
          {
            id: "createdDate",
            displayName: "Created Date",
            fieldName: "createdDate",
            system: true,
          },
          {
            id: "updatedDate",
            displayName: "Last Change Date",
            fieldName: "updatedDate",
            system: true,
          },
        ]);
    },
    // rights
    isQueryRight() {
      if (this.excludes && this.excludes.includes("query"))
        return false;

      return this.isCapableOf(SYSTEM_CAPABILITY.Query);
    },
    isSortRight() {
      if (this.excludes && this.excludes.includes("sort"))
        return false;

      return this.isCapableOf(SYSTEM_CAPABILITY.Sort);
    },
    isRefreshRight() {
      if (this.excludes && this.excludes.includes("refresh"))
        return false;

      return true;
    },
    isImportRight() { 
      if (this.excludes && this.excludes.includes("import") || !this.moduleImport)
        return false;

      return this.isCapableOf(SYSTEM_CAPABILITY.Write);
    },
    isExportRight() {
      // temp to disable for everything listing
      if (this.listingId === "everything" && !this.isInternal)
        return false;

      if (this.excludes && this.excludes.includes("export") || !Array.isArray(this.standardActivities))
        return false;
      
      return this.standardActivities.includes("export");
    },
    isDashboardWidgetRight() {
      // temp to disable for everything listing
      if (this.listingId === "everything" && !this.isInternal)
        return false;

      if (this.excludes && this.excludes.includes("dashboardWidget"))
        return false;

      return true;
    },
    isDashboardCalendarRight() {
      if (this.excludes && this.excludes.includes("dashboardCalendar"))
        return false;

      return true;
    },
    isBulkRight() {
      if (this.excludes && this.excludes.includes("bulk"))
        return false;

      return true;
    },
    mutableSorts: {
      get() {
        return this.sorts;
      },
      set(val) {
        this.$emit("update:sorts", val);
      }
    }
  },
  watch: {
    filters: {
      deep: true,
      handler(val) {
        this.debouncedFilters.cancel();

        if (this.skipFiltersWatch) return;

        this.debouncedFilters(val);
      }
    }
  },
  created() {
    this.debouncedFilters = _.debounce(async (filters) => {
      this.$emit("filters:changed", filters);
    }, 1000);
  },
  beforeDestroy() {
    this.cancelDebouncedFilters();
  }
}
</script>

<style lang="scss">
.fts-listing-tools {
  margin-bottom: 0.75rem;
  .listing-tools-container {
    display: flex;
    flex-wrap: wrap-reverse;
    align-items: flex-start;
  }
  .listing-controls {
    flex: 1;
    max-width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
    margin-left: 0.5rem;
    > *:not(:first-child) {
      margin-left: 0.75rem;
    }
  }
  .listing-control {
    .listing-control-trigger {
      font-size: 1.125rem;
      color: var(--fts-default);
      cursor: pointer;
    }
    &.listing-control--active {
      .listing-control-trigger {
        color: var(--fts-primary);
      }
    }
    &.listing-control--loading {
      .listing-control-trigger {
        cursor: progress;
      }
    }
  }
}
</style>